"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Search, X, FileText, CalendarDays, BookOpen, Loader2, ArrowRight } from "lucide-react";
import { createPortal } from "react-dom";

type Result = {
  type: "article" | "event" | "page";
  title: string;
  href: string;
  excerpt: string;
};

const TYPE_META = {
  article: { label: "Actualité",   icon: FileText,     color: "bg-blue-50 text-blue-600" },
  event:   { label: "Événement",   icon: CalendarDays, color: "bg-violet-50 text-violet-600" },
  page:    { label: "Page",        icon: BookOpen,     color: "bg-slate-100 text-slate-500" },
};

export default function SearchBar() {
  const [open, setOpen]       = useState(false);
  const [query, setQuery]     = useState("");
  const [results, setResults] = useState<Result[]>([]);
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const inputRef   = useRef<HTMLInputElement>(null);
  const router     = useRouter();
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => { setMounted(true); }, []);

  const search = useCallback(async (q: string) => {
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const res  = await fetch(`/api/search?q=${encodeURIComponent(q)}`);
      const data = await res.json();
      setResults(data.results ?? []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), 280);
    return () => clearTimeout(debounceRef.current);
  }, [query, search]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 60);
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") handleClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  function handleClose() {
    setOpen(false);
    setQuery("");
    setResults([]);
  }

  const grouped = {
    article: results.filter((r) => r.type === "article"),
    event:   results.filter((r) => r.type === "event"),
    page:    results.filter((r) => r.type === "page"),
  };

  const modal = open && (
    <div className="fixed inset-0 z-[9998] flex flex-col items-center justify-start pt-[10vh] px-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/70"
        onClick={handleClose}
      />

      {/* Panel */}
      <div
        className="relative w-full max-w-2xl overflow-hidden rounded-3xl shadow-2xl"
        style={{ background: "linear-gradient(160deg, #1a2744 0%, #1e3a5f 100%)" }}
      >
        {/* Header avec logo */}
        <div className="flex items-center gap-4 border-b border-white/10 px-5 py-4">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/logo.png"
            alt=""
            className="h-10 w-auto shrink-0 object-contain drop-shadow-lg"
            onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
          />
          <div className="flex flex-1 items-center gap-3 rounded-2xl bg-white/10 px-4 py-3">
            <Search size={18} className="shrink-0 text-white/50" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && query.trim().length >= 2) {
                  handleClose();
                  router.push(`/recherche?q=${encodeURIComponent(query.trim())}`);
                }
              }}
              placeholder="Rechercher sur le site…"
              className="flex-1 bg-transparent text-base text-white outline-none placeholder:text-white/40"
            />
            {loading && <Loader2 size={16} className="shrink-0 animate-spin text-white/40" />}
            {query && !loading && (
              <button onClick={() => { setQuery(""); setResults([]); inputRef.current?.focus(); }} className="text-white/40 hover:text-white/80 transition">
                <X size={16} />
              </button>
            )}
          </div>
          <button
            onClick={handleClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-white/50 transition hover:bg-white/10 hover:text-white"
          >
            <X size={18} />
          </button>
        </div>

        {/* Résultats */}
        <div className="overflow-y-auto" style={{ maxHeight: "60vh" }}>
          {/* État vide — invite */}
          {!query && (
            <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10">
                <Search size={24} className="text-white/50" />
              </div>
              <p className="text-sm text-white/50">
                Recherchez dans les actualités, l'agenda et les pages de la commune.
              </p>
            </div>
          )}

          {/* Aucun résultat */}
          {query.length >= 2 && !loading && results.length === 0 && (
            <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
              <p className="text-sm text-white/60">Aucun résultat pour <span className="font-medium text-white">« {query} »</span></p>
              <p className="text-xs text-white/30">Essayez avec d'autres mots-clés</p>
            </div>
          )}

          {/* Groupes de résultats */}
          {results.length > 0 && (
            <div className="p-3 space-y-1">
              {(["article", "event", "page"] as const).map((type) => {
                const group = grouped[type];
                if (group.length === 0) return null;
                const meta = TYPE_META[type];
                const Icon = meta.icon;
                return (
                  <div key={type}>
                    <p className="px-3 pb-1 pt-3 text-[10px] font-bold uppercase tracking-widest text-white/30">
                      {meta.label}s
                    </p>
                    {group.map((r, i) => (
                      <Link
                        key={i}
                        href={r.href}
                        onClick={handleClose}
                        className="group flex items-center gap-3 rounded-2xl px-3 py-3 transition hover:bg-white/10"
                      >
                        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ${meta.color}`}>
                          <Icon size={14} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-white">{r.title}</p>
                          {r.excerpt && (
                            <p className="mt-0.5 truncate text-xs text-white/45">{r.excerpt}</p>
                          )}
                        </div>
                        <ArrowRight size={14} className="shrink-0 text-white/20 transition group-hover:text-white/60" />
                      </Link>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-white/10 px-5 py-3 text-xs text-white/25">
          Courcelles-lès-Lens — Site officiel
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Bouton déclencheur dans le header */}
      <button
        onClick={() => setOpen(true)}
        className="flex h-10 w-10 items-center justify-center rounded-xl text-white/70 transition hover:bg-white/10 hover:text-white lg:h-auto lg:w-auto lg:gap-2 lg:rounded-xl lg:border lg:border-white/20 lg:bg-white/10 lg:px-4 lg:py-2 lg:text-sm"
        aria-label="Rechercher"
      >
        <Search size={16} className="shrink-0" />
        <span className="hidden lg:inline text-white/80">Rechercher…</span>
      </button>

      {mounted && createPortal(modal, document.body)}
    </>
  );
}
